// /en/expat-relocation/$topic/$city — EN long-tail combo page. Phase P-EXPAT-CITY-COMBOS.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ExpatTopic, ExpatCategory } from "@/data/expat-relocation";
import { EXPAT_TOPICS_LITE, EXPAT_LITE_BY_CATEGORY, EXPAT_CAT_LABELS_TH, EXPAT_CAT_LABELS_EN } from "@/data/expat-index.gen";
import { EXPAT_CITIES, type ExpatCity } from "@/data/expat-cities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, AlertTriangle, MapPin, Building2, Wallet, Wifi, Cloud, Users, ChevronRight, CheckCircle2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/expat-relocation/$topic/$city")({
  loader: async ({ params }) => {
    const { EXPAT_TOPICS } = await import("@/data/expat-relocation");
    const topic = EXPAT_TOPICS.find((t) => t.slug === params.topic);
    const { findExpatCity } = await import("@/data/expat-cities");
    const city = findExpatCity(params.city);
    if (!topic || !city) throw notFound();
    return { topic, city };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { topic: t, city: c } = loaderData;
    const url = `${SITE}/en/expat-relocation/${t.slug}/${c.slug}`;
    const snippet = c.categories[t.category];
    const title = `${t.titleEn} in ${c.nameEn} — Costs, Local Tips & Setup 2026 | NYC`;
    const desc = `${snippet.introEn.slice(0, 140)}... Ask us before you file — it's free.`.slice(0, 158);
    const localFaqs = buildLocalFaqsEn(t, c);
    return {
      meta: [
        { title: ctaTitle(title, "/en/expat-relocation/$topic/$city", "en") }, { name: "description", content: ctaDesc(desc, "/en/expat-relocation/$topic/$city", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/expat-relocation/$topic/$city", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/expat-relocation/$topic/$city", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/expat-relocation/${t.slug}/${c.slug}`, `/en/expat-relocation/${t.slug}/${c.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${t.titleEn} in ${c.nameEn}`, serviceType: t.titleEn, description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: { "@type": "City", name: c.nameEn, geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng } },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Place", "@id": `${url}#place`,
              name: c.nameEn, address: { "@type": "PostalAddress", addressLocality: c.nameEn, addressRegion: c.region, addressCountry: "TH" },
              geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng },
            }),
          },
          faqPageSchema(localFaqs),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Expat Relocation", item: `${SITE}/en/expat-relocation` },
                { "@type": "ListItem", position: 3, name: t.titleEn, item: `${SITE}/en/expat-relocation/${t.slug}` },
                { "@type": "ListItem", position: 4, name: c.nameEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">Page not found</h1>
      <Link to="/en/expat-relocation" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">Back to hub</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">An error occurred</h1>
      <Link to="/en/expat-relocation" className="mt-4 inline-block text-primary">Back to hub</Link>
    </main>
  ),
  component: ExpatComboEn,
});

function buildLocalFaqsEn(t: ExpatTopic, c: ExpatCity) {
  const s = c.categories[t.category];
  return [
    { q: `How much does ${t.titleEn.toLowerCase()} cost in ${c.nameEn}?`, a: `${s.costNoteEn}. National benchmark: ${t.costEn}.` },
    { q: `Which neighbourhoods in ${c.nameEn} are best for expats?`, a: `Top picks: ${c.districtsEn.slice(0, 4).join(", ")}.` },
    { q: `How long does ${t.titleEn.toLowerCase()} take?`, a: `Standard timeline ${t.timelineEn}. ${c.nameEn} is similar — NYC can pre-book to complete in a single day.` },
    { q: `What about internet and getting around in ${c.nameEn}?`, a: `${c.internetEn}. Transit: ${c.transitEn}.` },
    { q: `When is the best season in ${c.nameEn}?`, a: c.climateEn },
    ...t.faqsEn.slice(0, 3),
  ];
}

function ExpatComboEn() {
  const { topic: t, city: c } = Route.useLoaderData() as { topic: ExpatTopic; city: ExpatCity };
  const s = c.categories[t.category];
  const otherCities = EXPAT_CITIES.filter((x) => x.slug !== c.slug).slice(0, 6);
  const otherTopicsSameCat = EXPAT_TOPICS_LITE.filter((x) => x.category === t.category && x.slug !== t.slug).slice(0, 5);

  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/en/expat-relocation" className="hover:text-primary">Expat Relocation</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/en/expat-relocation/$topic" params={{ topic: t.slug }} className="hover:text-primary">{t.titleEn}</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <span>{c.nameEn}</span>
          </nav>
          <div className="text-xs font-bold uppercase tracking-widest text-primary">{EXPAT_CAT_LABELS_EN[t.category]} · {c.nameEn}</div>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-5xl">{t.titleEn} in {c.nameEn}</h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">{t.taglineEn}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <p className="text-sm leading-relaxed text-foreground sm:text-base">{s.introEn}</p>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-4 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3">
              <Wallet className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Local cost</div>
              <div className="text-xs font-bold leading-tight">{s.costNoteEn}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <MapPin className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Region</div>
              <div className="text-sm font-bold">{c.region}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Users className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Cost Index (BKK=100)</div>
              <div className="text-sm font-bold">{c.costIndex}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Cloud className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Climate</div>
              <div className="text-[11px] font-semibold leading-tight">{c.climateEn.slice(0, 60)}</div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold"><Building2 className="mr-2 inline h-5 w-5 text-primary" />Recommended places & options in {c.nameEn}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{c.profileEn}</p>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {s.placesEn.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">{c.nameEn}-specific tips</h2>
              <ol className="mt-4 space-y-3">
                {s.tipsEn.map((tip, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
                      <p className="text-sm text-foreground">{tip}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">General steps ({t.stepsEn.length} steps)</h2>
              <ol className="mt-4 space-y-3">
                {t.stepsEn.map((step, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold">{i + 1}</div>
                      <div>
                        <div className="font-bold">{step.name}</div>
                        <p className="mt-1 text-sm text-muted-foreground">{step.text}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">Top neighbourhoods in {c.nameEn}</h2>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {c.districtsEn.map((d, i) => (
                  <li key={i} className="rounded-lg border border-border bg-card p-3 text-sm font-semibold">{d}</li>
                ))}
              </ul>
              <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 text-sm">
                <div className="font-bold">Expat communities</div>
                <p className="mt-1 text-muted-foreground">{c.expatCommunitiesEn}</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">Frequently asked questions</h2>
              <div className="mt-4 space-y-3">
                {buildLocalFaqsEn(t, c).map((f, i) => (
                  <details key={i} className="rounded-xl border border-border bg-card p-4" open={i === 0}>
                    <summary className="cursor-pointer font-bold">{f.q}</summary>
                    <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-5">
              <h3 className="flex items-center gap-2 font-bold text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" /> Watch out
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">{t.warnEn}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold flex items-center gap-2"><Wifi className="h-4 w-4 text-primary" />Internet & transit</h3>
              <p className="mt-2 text-xs text-muted-foreground"><b>Internet:</b> {c.internetEn}</p>
              <p className="mt-1 text-xs text-muted-foreground"><b>Transit:</b> {c.transitEn}</p>
            </div>
            {t.servicePriceTHB > 0 && (
              <div className="rounded-2xl border border-primary bg-primary/5 p-5">
                <h3 className="font-bold">NYC service in {c.nameEn}</h3>
                <p className="mt-2 text-2xl font-extrabold text-primary">THB {t.servicePriceTHB.toLocaleString()}</p>
                <p className="mt-1 text-xs text-muted-foreground">Docs + agency liaison + pickup/delivery</p>
                <a href="tel:+66922429293" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
                  <Phone className="h-4 w-4" /> Call us
                </a>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl space-y-8">
          <div>
            <h2 className="text-xl font-extrabold">{t.titleEn} in other cities</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherCities.map((oc) => (
                <Link key={oc.slug} to="/en/expat-relocation/$topic/$city" params={{ topic: t.slug, city: oc.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><span className="font-bold text-sm">{t.titleEn} in {oc.nameEn}</span></div>
                  <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{oc.categories[t.category].costNoteEn}</div>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">View <ArrowRight className="h-3 w-3" /></span>
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-xl font-extrabold">More {EXPAT_CAT_LABELS_EN[t.category]} topics in {c.nameEn}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherTopicsSameCat.map((ot) => (
                <Link key={ot.slug} to="/en/expat-relocation/$topic/$city" params={{ topic: ot.slug, city: c.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                  <div className="flex items-center gap-2"><span className="text-xl">{ot.icon}</span><span className="font-bold text-sm">{ot.titleEn}</span></div>
                  <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{ot.taglineEn}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
